import { useState } from 'react';
import { Badge } from '@rudi/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@rudi/ui/table';
import { QueryState } from '../components/page';
import { useIdentityEntities } from '../api/hooks';
import type { IdentityListParams } from '../api/types';
import { DataQualityHeader } from './DataQualityOverviewPage';
import { EntityFilters, EntityLink, PAGE_SIZE, Pager, loadError } from './medical-data-quality';

export function InvestigatorsPage() {
  const [params, setParams] = useState<IdentityListParams>({
    sort: 'name',
    limit: PAGE_SIZE,
    offset: 0,
  });
  const persons = useIdentityEntities('person', params);
  const page = persons.data;
  const filtered = !!(params.q || params.flagged !== undefined);

  return (
    <div>
      <DataQualityHeader />
      <EntityFilters level="person" params={params} onChange={setParams} />
      <QueryState isLoading={persons.isLoading} error={loadError(persons.error)}>
        <div className="overflow-hidden rounded-3xl border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Investigator</TableHead>
                <TableHead>Organisations</TableHead>
                <TableHead className="text-right">Studies</TableHead>
                <TableHead>Emails</TableHead>
                <TableHead>Flags</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {page?.items.map(person => (
                <TableRow key={person.id}>
                  <TableCell className="min-w-[14rem]">
                    <div className="font-medium">
                      <EntityLink kind="person" id={person.id}>
                        {person.display_name}
                      </EntityLink>
                    </div>
                    <div className="text-xs text-muted-foreground">{person.slug}</div>
                    {person.spellings.some(spelling => spelling !== person.display_name) && (
                      <div className="text-xs text-muted-foreground">
                        Also written {person.spellings.join(', ')}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="max-w-[20rem]">
                    {person.organisations.length ? (
                      <ul className="text-sm">
                        {person.organisations.map(name => (
                          <li key={name}>{name}</li>
                        ))}
                      </ul>
                    ) : (
                      <span className="text-muted-foreground">None</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {person.study_count}
                    <div className="text-xs text-muted-foreground">
                      {person.mention_count} Mentions
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">
                    {person.emails.length ? (
                      person.emails.map(email => <div key={email}>{email}</div>)
                    ) : (
                      <span className="text-muted-foreground">None</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {person.open_flags > 0 ? (
                      <Badge variant="destructive">{person.open_flags} open</Badge>
                    ) : (
                      <span className="text-muted-foreground">None</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {page?.items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="h-28 text-center text-muted-foreground">
                    {filtered
                      ? 'No Investigators match these filters.'
                      : 'No Investigators resolved yet.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        {page && (
          <Pager
            total={page.total}
            limit={page.limit}
            offset={page.offset}
            onChange={offset => setParams(previous => ({ ...previous, offset }))}
          />
        )}
      </QueryState>
    </div>
  );
}
